/* Physical wall-art print catalog — Printful fulfilment (Rina ask,
 * 2026-06-14). Turns any digital wall-art design into a real printed,
 * framed, shipped product. Pure data + helpers — NO secrets, NO env,
 * NO API client — so it's safe to import from both the client
 * PhysicalPrintPanel and the server checkout/webhook routes.
 *
 * Variant IDs are REAL Printful catalog variant IDs, pulled live from
 * https://api.printful.com/products/{1,2,3} on 2026-06-14 (the catalog
 * endpoint is public). The Printful order is placed server-side from the
 * Stripe webhook using PRINTFUL_API_KEY + PRINTFUL_STORE_ID (backend env
 * only — see .env.local). Customer prices give ~55-65% margin over
 * Printful base cost; the storefront is the source of truth for price,
 * Printful only sees the fulfilment cost.
 *
 *   Printful product 1 = Enhanced Matte Paper Poster (unframed)
 *   Printful product 2 = Enhanced Matte Paper Framed Poster (black frame)
 *   Printful product 3 = Canvas (stretched gallery wrap)
 */

export type PrintFinish = "poster" | "framed" | "canvas";
export type PrintSize = "11x14" | "16x20" | "18x24" | "24x36";

export type PrintVariant = {
  size: PrintSize;
  label: string; // human size label, e.g. "16″ × 20″"
  priceCents: number; // customer-facing Apex price
  printfulVariantId: number; // REAL Printful catalog variant id
};

export type PrintFinishDef = {
  finish: PrintFinish;
  label: string;
  blurb: string;
  printfulProductId: number;
  /* Printful color attribute, when the product has one (framed poster
   * defaults to black). null when the product has no color variants. */
  printfulColor: string | null;
  variants: PrintVariant[];
};

export const PRINT_FINISHES: PrintFinishDef[] = [
  {
    finish: "framed",
    label: "Framed print",
    blurb:
      "Museum-grade matte paper in a real wood frame with shatterproof glazing — ready to hang out of the box.",
    printfulProductId: 2,
    printfulColor: "Black",
    variants: [
      { size: "11x14", label: "11″ × 14″", priceCents: 5900, printfulVariantId: 14292 },
      { size: "16x20", label: "16″ × 20″", priceCents: 7400, printfulVariantId: 4399 },
      { size: "18x24", label: "18″ × 24″", priceCents: 8900, printfulVariantId: 3 },
      { size: "24x36", label: "24″ × 36″", priceCents: 12900, printfulVariantId: 4 },
    ],
  },
  {
    finish: "canvas",
    label: "Canvas",
    blurb:
      "Gallery-wrapped stretched canvas on a solid wood frame — no glass, no glare, ready to hang.",
    printfulProductId: 3,
    printfulColor: null,
    variants: [
      { size: "11x14", label: "11″ × 14″", priceCents: 5900, printfulVariantId: 19298 },
      { size: "16x20", label: "16″ × 20″", priceCents: 7400, printfulVariantId: 6 },
      { size: "18x24", label: "18″ × 24″", priceCents: 9400, printfulVariantId: 7 },
      { size: "24x36", label: "24″ × 36″", priceCents: 14900, printfulVariantId: 825 },
    ],
  },
  {
    finish: "poster",
    label: "Poster (unframed)",
    blurb:
      "Heavyweight enhanced-matte archival paper, giclée-printed. Frame it yourself — the budget-friendly entry.",
    printfulProductId: 1,
    printfulColor: null,
    variants: [
      { size: "11x14", label: "11″ × 14″", priceCents: 2400, printfulVariantId: 14125 },
      { size: "16x20", label: "16″ × 20″", priceCents: 3200, printfulVariantId: 3877 },
      { size: "18x24", label: "18″ × 24″", priceCents: 3900, printfulVariantId: 1 },
      { size: "24x36", label: "24″ × 36″", priceCents: 5400, printfulVariantId: 2 },
    ],
  },
];

const FINISH_BY_KEY = new Map(PRINT_FINISHES.map((f) => [f.finish, f]));

export function getPrintFinish(finish: string): PrintFinishDef | null {
  return FINISH_BY_KEY.get(finish as PrintFinish) ?? null;
}

/* Resolve a (finish, size) pair to its full row — the single source of
 * truth the checkout route trusts for price + Printful variant. Returns
 * null for any combo not in the catalog (never trust a client price). */
export function resolvePrintVariant(
  finish: string,
  size: string,
): { def: PrintFinishDef; variant: PrintVariant } | null {
  const def = getPrintFinish(finish);
  if (!def) return null;
  const variant = def.variants.find((v) => v.size === size);
  if (!variant) return null;
  return { def, variant };
}

/* Cheapest framed price — used for the "from $X" teaser on the PDP. */
export function fromPriceCents(finish: PrintFinish = "framed"): number {
  const def = getPrintFinish(finish);
  if (!def) return 0;
  return Math.min(...def.variants.map((v) => v.priceCents));
}

/* ── Printed stickers (Printful) ─────────────────────────────────────────
 * Brian, 2026-09-06: "they can't just buy a PDF — they gotta buy the actual
 * sticker pack." Two physical products, same fulfilment path as framed
 * wall art (shop checkout `print` metadata → Stripe collects the address →
 * webhook places the Printful order):
 *
 *   sheet    Printful 505 "Kiss-Cut Sticker Sheet", 5.83″×8.27″, one print
 *            file we compose ourselves: 12 designs of a collection (or 12 of
 *            one design) laid out at 300 DPI — /api/stickers/sheet.
 *   diecut   Printful 957 "Die-Cut Stickers", satin, one design per sticker.
 *            Only 3″ and 4″ are offered: the source PNGs are 1024 px, which
 *            is 341 DPI at 3″ and 256 DPI at 4″; larger would print soft.
 *
 * Variant ids and base costs pulled live from api.printful.com/products/
 * {505,957} on 2026-09-06 (public endpoint). Shipping is charged as its own
 * line at checkout (STICKER_SHIPPING_CENTS, US only for now) so the sticker
 * prices can stay honest and low. Margin after Printful base + shipping:
 * sheet ≈ $3.85, die-cut 3″ ≈ $2.63, 4″ ≈ $3.15. */
export type StickerPrintFinish = "sheet" | "diecut";
export type StickerPrintVariant = {
  size: string;
  label: string;
  priceCents: number;
  printfulVariantId: number;
  printfulBaseCents: number;
};
export type StickerPrintDef = {
  finish: StickerPrintFinish;
  label: string;
  blurb: string;
  printfulProductId: number;
  variants: StickerPrintVariant[];
};
export const STICKER_SHIPPING_CENTS = 399;
export const STICKER_SHEET_DESIGNS = 12;
export const STICKER_PRINTS: StickerPrintDef[] = [
  {
    finish: "sheet",
    label: "Printed sticker sheet",
    blurb: "12 stickers on one 5.83″ × 8.27″ kiss-cut sheet — peel each one off the backing.",
    printfulProductId: 505,
    variants: [{ size: "sheet", label: "5.83″ × 8.27″ sheet · 12 stickers", priceCents: 1299, printfulVariantId: 12917, printfulBaseCents: 515 }],
  },
  {
    finish: "diecut",
    label: "Die-cut sticker",
    blurb: "One design, cut to its shape on satin vinyl.",
    printfulProductId: 957,
    variants: [
      { size: "3x3", label: "3″ × 3″", priceCents: 699, printfulVariantId: 24965, printfulBaseCents: 436 },
      { size: "4x4", label: "4″ × 4″", priceCents: 799, printfulVariantId: 24966, printfulBaseCents: 484 },
    ],
  },
];
export function resolveStickerPrint(finish: string, size: string): { def: StickerPrintDef; variant: StickerPrintVariant } | null {
  const def = STICKER_PRINTS.find((d) => d.finish === finish);
  if (!def) return null;
  const variant = def.variants.find((v) => v.size === size);
  if (!variant) return null;
  return { def, variant };
}
